/**
 * Budget detailverdeling designer.
 *
 * Fast UI/UX to split a strategisch budget across renovation labels and
 * units. Drag a label from the left palette into the canvas to add a row.
 * Drag a unit from the top bar onto a row to attach it as a kostenplaats —
 * with auto-equal split + per-unit amount/percentage overrides.
 *
 * Saving fires a single bulk POST to the bucket's `/split` endpoint. Rows with
 * multiple units are expanded into one InvestmentItem per unit so each unit
 * gets its own accounting line carrying its share of the row amount.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import {
  ArrowsCounterClockwise,
  Coin,
  Cube,
  ListBullets,
  Plus,
  Stack,
  Tag,
  Trash,
  Wallet,
  X,
} from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  RenovationLabel,
  InvestmentItemBudgetLabel,
} from "screens/investment/types/investment";
import { BudgetBucketView } from "screens/investment/types/budget-bucket";
import { UnitDTO } from "shared/types/unit";
import {
  formatCurrency,
  getUnitSurface,
  LABEL_TYPE_TONE,
  equalSplitPercentages,
  newDraftId,
  SplitItemPayload,
} from "./budgetSplitShared";
import {
  KostenplaatsVerdelingCanvas,
  KostenplaatsGroup,
} from "./KostenplaatsVerdelingCanvas";
import { UnitVerdelingSummary } from "./UnitVerdelingSummary";

interface SplitRow {
  rowId: string;
  labelId: number | null;
  labelName: string;
  labelType?: string;
  title: string;
  /**
   * Free-form description. Persisted as `InvestmentItem.description` so the
   * line keeps its scope notes after creation. Defaults to empty.
   */
  description: string;
  amount: number;
  unitIds: string[];
  /** Explicit % per unit. Sum should equal 100; auto-balanced on add/remove. */
  unitPercentages: Record<string, number>;
  budgetLabel: InvestmentItemBudgetLabel | null;
}

interface BudgetSplitDesignerProps {
  bucket: BudgetBucketView | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved?: (createdIds: number[]) => void;
  /**
   * When provided AND no bucket is pre-selected, the designer fetches all
   * buckets on this building and shows an in-modal pot picker so the user
   * can choose where the new lines should land. "Overflow" is a special
   * option that creates lines without a bucket (BE routes them to the
   * onvoorzien pot via the budgetLabel rule).
   */
  buildingId?: string;
}

/** The two complementary verdeling-views the designer can render. */
type VerdelingView = "label" | "kostenplaats";

export const BudgetSplitDesigner: React.FC<BudgetSplitDesignerProps> = ({
  bucket,
  isOpen,
  onClose,
  onSaved,
  buildingId,
}) => {
  const { session } = useSession();

  const [labels, setLabels] = useState<RenovationLabel[]>([]);
  const [labelsLoading, setLabelsLoading] = useState(false);
  const [units, setUnits] = useState<UnitDTO[]>([]);

  const [rows, setRows] = useState<SplitRow[]>([]);
  // Kostenplaats-first draft (the alternative, unit-first verdeling view).
  const [unitGroups, setUnitGroups] = useState<KostenplaatsGroup[]>([]);
  const [viewMode, setViewMode] = useState<VerdelingView>("label");
  // Read-only "verdeling per kostenplaats" rollup, shown beside the canvas.
  const [showUnitSummary, setShowUnitSummary] = useState(false);
  const [labelSearch, setLabelSearch] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Bucket the designer is currently writing to. Defaults to the prop, but
  // the user can swap it via the in-header pot picker. When the prop is null
  // and `buildingId` is provided we drive a building-wide picker instead.
  const [currentBucket, setCurrentBucket] =
    useState<BudgetBucketView | null>(bucket);
  const [availableBuckets, setAvailableBuckets] = useState<BudgetBucketView[]>(
    [],
  );

  const effectiveBuildingId = bucket?.buildingId || buildingId;

  // Reset draft state every time the dialog opens with a fresh bucket.
  useEffect(() => {
    if (!isOpen) return;
    setRows([]);
    setUnitGroups([]);
    setViewMode("label");
    setShowUnitSummary(false);
    setLabelSearch("");
    setCurrentBucket(bucket);
  }, [isOpen, bucket?.id]);

  // Load all buckets on the building so the in-header picker has something
  // to show. Only runs when we don't have a hard-pinned bucket already.
  useEffect(() => {
    if (!isOpen || !session || !effectiveBuildingId) return;
    let cancelled = false;
    fetchWithAuth(
      `${API_URL}/investment/budget-buckets?buildingId=${effectiveBuildingId}`,
      { method: "GET" },
      session,
    )
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (cancelled) return;
        const list: BudgetBucketView[] = Array.isArray(data) ? data : [];
        // Investeringslijnen mogen nooit op de overflow-pot landen — de
        // overflow ontstaat enkel als gevolg van projectoverschrijdingen.
        setAvailableBuckets(list.filter((b) => !b.isOverflow));
      })
      .catch((err) => console.error("Error loading buckets:", err));
    return () => {
      cancelled = true;
    };
  }, [isOpen, session, effectiveBuildingId]);

  // Load renovation labels (once per open).
  useEffect(() => {
    if (!isOpen || !session) return;
    let cancelled = false;
    setLabelsLoading(true);
    fetchWithAuth(`${API_URL}/renovation/labels`, { method: "GET" }, session)
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled && Array.isArray(data)) setLabels(data);
      })
      .catch((err) => console.error("Error loading labels:", err))
      .finally(() => {
        if (!cancelled) setLabelsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, session]);

  // Load units of the bucket's building.
  useEffect(() => {
    if (!isOpen || !effectiveBuildingId || !session) return;
    let cancelled = false;
    fetchWithAuth(
      `${API_URL}/units/building/${effectiveBuildingId}`,
      { method: "GET" },
      session,
    )
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : data.data || [];
        setUnits(list);
      })
      .catch((err) => console.error("Error loading units:", err));
    return () => {
      cancelled = true;
    };
  }, [isOpen, effectiveBuildingId, session]);

  // Quick lookup by id for surface/details.
  const unitsById = useMemo(() => {
    const m = new Map<string, UnitDTO>();
    for (const u of units) {
      if (u.id) m.set(u.id, u);
    }
    return m;
  }, [units]);

  const labelTotal = useMemo(
    () => rows.reduce((s, r) => s + (Number(r.amount) || 0), 0),
    [rows],
  );
  const kostenplaatsTotal = useMemo(
    () =>
      unitGroups.reduce(
        (s, g) =>
          s + g.allocations.reduce((ss, a) => ss + (Number(a.amount) || 0), 0),
        0,
      ),
    [unitGroups],
  );
  const totalDraft = viewMode === "label" ? labelTotal : kostenplaatsTotal;

  const bucketRemaining = currentBucket?.remainingAmount ?? 0;
  const remaining = bucketRemaining - totalDraft;
  const overspent = remaining < 0;

  const labelsByType = useMemo(() => {
    const term = labelSearch.trim().toLowerCase();
    const filtered = !term
      ? labels
      : labels.filter((l) =>
          [l.name, l.type ?? ""].join(" ").toLowerCase().includes(term),
        );
    const groups = new Map<string, RenovationLabel[]>();
    for (const l of filtered) {
      const type = (l.type as any) || "algemeen";
      if (!groups.has(type)) groups.set(type, []);
      groups.get(type)!.push(l);
    }
    return Array.from(groups.entries()).map(([type, items]) => ({
      type,
      items: items.sort((a, b) => a.name.localeCompare(b.name)),
    }));
  }, [labels, labelSearch]);

  const addRowFromLabel = useCallback((label: RenovationLabel) => {
    setRows((prev) => [
      ...prev,
      {
        rowId: newDraftId("r"),
        labelId: label.id,
        labelName: label.name,
        labelType: (label as any).type,
        title: `Budget voor ${label.name}`,
        description: "",
        amount: 0,
        unitIds: [],
        unitPercentages: {},
        // Bucket-derived rows are always strategic by default.
        budgetLabel: InvestmentItemBudgetLabel.STRATEGISCH,
      },
    ]);
  }, []);

  const updateRow = useCallback(
    (rowId: string, patch: Partial<SplitRow>) => {
      setRows((prev) =>
        prev.map((r) => (r.rowId === rowId ? { ...r, ...patch } : r)),
      );
    },
    [],
  );

  const removeRow = useCallback((rowId: string) => {
    setRows((prev) => prev.filter((r) => r.rowId !== rowId));
  }, []);

  const splitRemainingEqually = useCallback(() => {
    setRows((prev) => {
      if (!prev.length) return prev;
      const share = Math.floor(bucketRemaining / prev.length);
      return prev.map((r) => ({ ...r, amount: share }));
    });
  }, [bucketRemaining]);

  const fillRestOnRow = useCallback(
    (rowId: string) => {
      setRows((prev) => {
        const otherSum = prev.reduce(
          (s, r) => (r.rowId === rowId ? s : s + (Number(r.amount) || 0)),
          0,
        );
        const room = bucketRemaining - otherSum;
        return prev.map((r) =>
          r.rowId === rowId ? { ...r, amount: Math.max(0, room) } : r,
        );
      });
    },
    [bucketRemaining],
  );

  // ---- Unit assignment helpers ------------------------------------------
  const addUnitToRow = useCallback(
    (rowId: string, unitId: string) => {
      setRows((prev) =>
        prev.map((r) => {
          if (r.rowId !== rowId) return r;
          if (r.unitIds.includes(unitId)) return r;
          const nextUnitIds = [...r.unitIds, unitId];
          // Always re-balance to equal on add — keeps the math obvious.
          return {
            ...r,
            unitIds: nextUnitIds,
            unitPercentages: equalSplitPercentages(nextUnitIds),
          };
        }),
      );
    },
    [],
  );

  const removeUnitFromRow = useCallback((rowId: string, unitId: string) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.rowId !== rowId) return r;
        const nextUnitIds = r.unitIds.filter((id) => id !== unitId);
        return {
          ...r,
          unitIds: nextUnitIds,
          unitPercentages: equalSplitPercentages(nextUnitIds),
        };
      }),
    );
  }, []);

  const setUnitPercentage = useCallback(
    (rowId: string, unitId: string, pct: number) => {
      setRows((prev) =>
        prev.map((r) => {
          if (r.rowId !== rowId || !r.unitIds.includes(unitId)) return r;
          const safe = Math.max(0, Math.min(100, Number(pct) || 0));
          return {
            ...r,
            unitPercentages: { ...r.unitPercentages, [unitId]: safe },
          };
        }),
      );
    },
    [],
  );

  const setUnitAmount = useCallback(
    (rowId: string, unitId: string, amount: number) => {
      setRows((prev) =>
        prev.map((r) => {
          if (r.rowId !== rowId || !r.unitIds.includes(unitId)) return r;
          if (!r.amount || r.amount <= 0) return r;
          const pct = Math.max(
            0,
            Math.min(100, ((Number(amount) || 0) / r.amount) * 100),
          );
          return {
            ...r,
            unitPercentages: {
              ...r.unitPercentages,
              [unitId]: +pct.toFixed(2),
            },
          };
        }),
      );
    },
    [],
  );

  const splitUnitsEquallyOnRow = useCallback((rowId: string) => {
    setRows((prev) =>
      prev.map((r) =>
        r.rowId === rowId
          ? { ...r, unitPercentages: equalSplitPercentages(r.unitIds) }
          : r,
      ),
    );
  }, []);

  // Bulk: drop the "all units" pseudo-chip on a single row.
  const allUnitIds = useMemo(
    () => units.map((u) => u.id).filter((id): id is string => !!id),
    [units],
  );

  const applyAllUnitsToRow = useCallback(
    (rowId: string) => {
      if (allUnitIds.length === 0) return;
      setRows((prev) =>
        prev.map((r) => {
          if (r.rowId !== rowId) return r;
          const nextUnitIds = Array.from(
            new Set([...r.unitIds, ...allUnitIds]),
          );
          return {
            ...r,
            unitIds: nextUnitIds,
            unitPercentages: equalSplitPercentages(nextUnitIds),
          };
        }),
      );
    },
    [allUnitIds],
  );

  const applyAllUnitsToAllRows = useCallback(() => {
    if (allUnitIds.length === 0) return;
    setRows((prev) =>
      prev.map((r) => {
        const nextUnitIds = Array.from(new Set([...r.unitIds, ...allUnitIds]));
        return {
          ...r,
          unitIds: nextUnitIds,
          unitPercentages: equalSplitPercentages(nextUnitIds),
        };
      }),
    );
  }, [allUnitIds]);

  // ---- Drag & drop -------------------------------------------------------
  const dragLabelRef = useRef<RenovationLabel | null>(null);
  const dragAllUnitsRef = useRef<boolean>(false);
  const dragUnitRef = useRef<string | null>(null);

  const handleLabelDragStart = (label: RenovationLabel) => () => {
    dragLabelRef.current = label;
  };
  const handleLabelDragEnd = () => {
    dragLabelRef.current = null;
  };
  const handleAllUnitsDragStart = () => {
    dragAllUnitsRef.current = true;
  };
  const handleAllUnitsDragEnd = () => {
    dragAllUnitsRef.current = false;
  };
  const handleUnitDragStart = (unitId: string) => () => {
    dragUnitRef.current = unitId;
  };
  const handleUnitDragEnd = () => {
    dragUnitRef.current = null;
  };

  // Accessors shared with the kostenplaats-first canvas so it can react to the
  // same drag refs without duplicating drag bookkeeping.
  const getDraggedLabel = useCallback(() => dragLabelRef.current, []);
  const getDraggedUnit = useCallback(() => dragUnitRef.current, []);
  const isAllUnitsDrag = useCallback(() => dragAllUnitsRef.current, []);
  const clearDrag = useCallback(() => {
    dragLabelRef.current = null;
    dragUnitRef.current = null;
    dragAllUnitsRef.current = false;
  }, []);

  const handleCanvasDragOver = (e: React.DragEvent) => {
    if (
      dragLabelRef.current ||
      dragAllUnitsRef.current ||
      dragUnitRef.current
    ) {
      e.preventDefault();
    }
  };
  const handleCanvasDrop = (e: React.DragEvent) => {
    if (dragLabelRef.current) {
      e.preventDefault();
      addRowFromLabel(dragLabelRef.current);
      dragLabelRef.current = null;
      return;
    }
    if (dragAllUnitsRef.current) {
      e.preventDefault();
      applyAllUnitsToAllRows();
      dragAllUnitsRef.current = false;
      return;
    }
    if (dragUnitRef.current) {
      // Single unit dropped on canvas (not a row). Apply to all rows.
      e.preventDefault();
      const unitId = dragUnitRef.current;
      setRows((prev) =>
        prev.map((r) => {
          if (r.unitIds.includes(unitId)) return r;
          const nextUnitIds = [...r.unitIds, unitId];
          return {
            ...r,
            unitIds: nextUnitIds,
            unitPercentages: equalSplitPercentages(nextUnitIds),
          };
        }),
      );
      dragUnitRef.current = null;
    }
  };

  // ---- Save --------------------------------------------------------------
  // Both views build the SAME payload shape so the existing `/split` endpoint
  // is reused untouched. One InvestmentItem is created per entry.

  // Label-first view: one line per row — all selected units land on the same
  // line as a many-to-many. Per-unit % overrides ride along as
  // `unitPercentages` (design-time hint; BE may ignore unknown fields).
  const labelItems: SplitItemPayload[] = useMemo(
    () =>
      rows
        .filter((r) => Number(r.amount) > 0)
        .map((r) => ({
          labelId: r.labelId,
          amount: Number(r.amount) || 0,
          unitIds: r.unitIds,
          unitPercentages: r.unitIds.length > 1 ? r.unitPercentages : undefined,
          title: r.title,
          description: r.description?.trim() || undefined,
          budgetLabel: r.budgetLabel,
        })),
    [rows],
  );

  // Kostenplaats-first view: each planned work (label) inside a unit group
  // becomes one line scoped to that single unit.
  const kostenplaatsItems: SplitItemPayload[] = useMemo(
    () =>
      unitGroups.flatMap((g) =>
        g.allocations
          .filter((a) => Number(a.amount) > 0)
          .map((a) => ({
            labelId: a.labelId,
            amount: Number(a.amount) || 0,
            unitIds: [g.unitId],
            title: a.title,
            description: a.description?.trim() || undefined,
            budgetLabel: a.budgetLabel,
          })),
      ),
    [unitGroups],
  );

  const itemsToCreate =
    viewMode === "label" ? labelItems : kostenplaatsItems;

  const canSave =
    !!currentBucket && itemsToCreate.length > 0 && !overspent && !isSaving;

  const handleSave = async () => {
    if (!currentBucket || !session) return;
    if (itemsToCreate.length === 0) {
      toast.error("Voeg minstens één regel met een bedrag toe");
      return;
    }
    setIsSaving(true);
    try {
      let createdIds: number[] = [];

      const res = await fetchWithAuth(
        `${API_URL}/investment/budget-buckets/${currentBucket.id}/split`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items: itemsToCreate }),
        },
        session,
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to split budget");
      }
      const data = await res.json();
      createdIds = Array.isArray(data?.createdIds) ? data.createdIds : [];

      toast.success(
        `${createdIds.length} budgetlijn${
          createdIds.length === 1 ? "" : "en"
        } aangemaakt`,
      );
      onSaved?.(createdIds);
      onClose();
    } catch (err) {
      console.error("Error saving budget split:", err);
      toast.error(
        err instanceof Error ? err.message : "Fout bij opslaan verdeling",
      );
    } finally {
      setIsSaving(false);
    }
  };

  // ----- Sorted units list for the top bar -----
  // NB: keep this hook before any early `return null` so the hook order stays
  // stable across renders (otherwise React throws "Rendered more hooks…").
  const orderedUnits = useMemo(
    () =>
      units
        .filter((u): u is UnitDTO & { id: string } => !!u.id)
        .sort((a, b) => a.id.localeCompare(b.id)),
    [units],
  );

  // Render the dialog even when no bucket is pre-selected — the pot picker
  // covers that case in-place. We only short-circuit when neither a bucket
  // nor a building is provided (nothing to scope to).
  if (!effectiveBuildingId) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex h-[90vh] max-h-[90vh] flex-col p-0 sm:max-w-[1200px]">
        <DialogHeader className="border-b border-neutral-200 px-6 py-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Wallet
                size={22}
                weight="duotone"
                className="text-moss-primary"
              />
              <DialogTitle className="text-lg">
                Budget detailverdeling
                {currentBucket ? ` — ${currentBucket.name}` : ""}
              </DialogTitle>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={onClose}
                disabled={isSaving}
              >
                Annuleren
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={!canSave}
                className="bg-moss-primary hover:bg-moss-primary/90 text-white"
              >
                {isSaving
                  ? "Opslaan…"
                  : `Maak ${itemsToCreate.length} budgetlijn${
                      itemsToCreate.length === 1 ? "" : "en"
                    }`}
              </Button>
            </div>
          </div>
          <DialogDescription className="sr-only">
            Verdeel het strategisch budget over labels en units.
          </DialogDescription>

          {/* Verdeling-modus: label-eerst (klassiek) of kostenplaats-eerst.
              Beide modi schrijven naar hetzelfde /split-endpoint; wisselen
              behoudt de andere modus zijn eigen concept. Daarnaast een aparte
              schakelaar om de read-only verdeling per kostenplaats te tonen. */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <div className="inline-flex items-center gap-1 rounded-lg border border-neutral-200 bg-neutral-50 p-0.5">
              <button
                type="button"
                onClick={() => setViewMode("label")}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                  viewMode === "label"
                    ? "bg-moss-primary text-white shadow-sm"
                    : "text-neutral-secondary-mid hover:text-neutral-primary"
                }`}
                title="Start vanuit labels en koppel er kostenplaatsen aan"
              >
                <Tag
                  size={12}
                  weight={viewMode === "label" ? "fill" : "duotone"}
                />
                Per label
              </button>
              <button
                type="button"
                onClick={() => setViewMode("kostenplaats")}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                  viewMode === "kostenplaats"
                    ? "bg-moss-primary text-white shadow-sm"
                    : "text-neutral-secondary-mid hover:text-neutral-primary"
                }`}
                title="Start vanuit kostenplaatsen (units) en plan er werken op"
              >
                <Stack
                  size={12}
                  weight={viewMode === "kostenplaats" ? "fill" : "duotone"}
                />
                Per kostenplaats
              </button>
            </div>

            <button
              type="button"
              onClick={() => setShowUnitSummary((v) => !v)}
              aria-pressed={showUnitSummary}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                showUnitSummary
                  ? "border-moss-primary/40 bg-moss-primary/10 text-moss-primary"
                  : "border-neutral-200 bg-white text-neutral-secondary-mid hover:text-neutral-primary"
              }`}
              title="Toon of verberg de verdeling per kostenplaats"
            >
              <ListBullets
                size={12}
                weight={showUnitSummary ? "fill" : "duotone"}
              />
              Verdeling per kostenplaats
            </button>
          </div>

          {/* Strategisch budget-picker — chips per echte (niet-overflow)
              bucket. Investeringslijnen kunnen nooit naar de overflow gaan;
              die ontstaat enkel uit project-overschrijdingen. */}
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            <span className="text-neutral-secondary-mid text-[10px] font-semibold uppercase tracking-wide">
              Strategisch budget
            </span>
            {availableBuckets.length === 0 ? (
              <span className="text-neutral-secondary-mid text-xs italic">
                Geen strategisch budget gevonden
              </span>
            ) : (
              availableBuckets.map((b) => {
                const active = currentBucket?.id === b.id;
                return (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => setCurrentBucket(b)}
                    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors ${
                      active
                        ? "border-moss-primary/40 bg-moss-primary/10 text-moss-primary"
                        : "border-neutral-200 bg-white text-neutral-500 hover:border-neutral-300"
                    }`}
                    title={`${b.name}${b.year ? ` (${b.year})` : ""}`}
                  >
                    <Wallet size={10} weight={active ? "fill" : "duotone"} />
                    {b.name}
                  </button>
                );
              })
            )}
          </div>

          {currentBucket ? (
            <BudgetMeter
              total={Number(currentBucket.totalAmount) || 0}
              usedBeforeDraft={Number(currentBucket.usedAmount) || 0}
              draftAmount={totalDraft}
              remaining={remaining}
              overspent={overspent}
            />
          ) : (
            <div className="mt-3 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 text-xs text-neutral-secondary-mid">
              Kies een strategisch budget hierboven om met de verdeling te starten.
            </div>
          )}
        </DialogHeader>

        {/* TOP BAR: one chip per building unit — drag onto a row to attach. */}
        <div className="border-b border-neutral-200 bg-neutral-50 px-6 py-2.5">
          <div className="text-neutral-secondary-mid mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide">
            <Cube size={11} weight="duotone" />
            Units van dit gebouw
            <span className="text-neutral-400">({orderedUnits.length})</span>
            <span className="text-neutral-400 ml-2 normal-case font-normal italic tracking-normal">
              {viewMode === "label"
                ? "sleep een unit op een regel om er een kostenplaats van te maken"
                : "sleep een unit naar het canvas om er een kostenplaats van te maken"}
            </span>
          </div>
          {orderedUnits.length === 0 ? (
            <div className="text-neutral-secondary-mid py-1 text-xs italic">
              Geen units gevonden voor dit gebouw
            </div>
          ) : (
            <div className="flex max-h-[88px] flex-wrap gap-1.5 overflow-y-auto">
              {orderedUnits.map((u) => {
                const surface = getUnitSurface(u);
                return (
                  <button
                    key={u.id}
                    type="button"
                    draggable
                    onDragStart={handleUnitDragStart(u.id!)}
                    onDragEnd={handleUnitDragEnd}
                    className="bg-moss-primary/5 text-moss-primary border-moss-primary/30 hover:bg-moss-primary/10 inline-flex cursor-grab items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium transition-colors active:cursor-grabbing"
                    title={
                      surface
                        ? `${u.id} — ${surface} m²`
                        : `${u.id} — geen oppervlakte bekend`
                    }
                  >
                    <Cube size={11} weight="duotone" />
                    <span className="tabular-nums">{u.id}</span>
                    {surface !== null && (
                      <span className="text-neutral-secondary-mid border-l border-moss-primary/20 pl-1.5 text-[10px] tabular-nums">
                        {surface} m²
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div
          className={`grid flex-1 gap-4 overflow-hidden p-4 ${
            showUnitSummary
              ? "grid-cols-[260px_1fr_300px]"
              : "grid-cols-[260px_1fr]"
          }`}
        >
          {/* LEFT: label palette */}
          <aside className="flex flex-col gap-2 overflow-hidden rounded-lg border border-neutral-200 bg-neutral-50 p-3">
            {/* Bulk: all units of building */}
            <button
              type="button"
              draggable={allUnitIds.length > 0}
              onDragStart={handleAllUnitsDragStart}
              onDragEnd={handleAllUnitsDragEnd}
              onClick={() => {
                if (allUnitIds.length === 0) return;
                if (viewMode === "kostenplaats") {
                  setUnitGroups((prev) => {
                    const existing = new Set(prev.map((g) => g.unitId));
                    const additions = allUnitIds
                      .filter((id) => !existing.has(id))
                      .map((unitId) => ({ unitId, allocations: [] }));
                    return additions.length ? [...prev, ...additions] : prev;
                  });
                  return;
                }
                if (rows.length === 0) {
                  toast.info(
                    "Voeg eerst een regel toe, sleep dan deze chip om alle units te koppelen",
                  );
                  return;
                }
                applyAllUnitsToAllRows();
              }}
              disabled={allUnitIds.length === 0}
              className={`flex items-center justify-center gap-1.5 rounded-md border-2 border-dashed px-2 py-2 text-xs font-semibold transition-colors ${
                allUnitIds.length === 0
                  ? "cursor-not-allowed border-neutral-200 bg-neutral-100 text-neutral-400"
                  : "border-moss-primary/40 bg-moss-primary/5 text-moss-primary hover:bg-moss-primary/10 cursor-grab active:cursor-grabbing"
              }`}
              title={
                allUnitIds.length === 0
                  ? "Geen units gevonden"
                  : "Sleep om alle units van dit gebouw als kostenplaatsen toe te voegen"
              }
            >
              <Cube size={14} weight="duotone" />
              Alle units ({allUnitIds.length})
            </button>

            <div className="text-neutral-secondary-mid mt-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide">
              <Tag size={12} weight="duotone" />
              Labels
            </div>
            <Input
              value={labelSearch}
              onChange={(e) => setLabelSearch(e.target.value)}
              placeholder="Zoek label..."
              className="h-8 text-sm"
            />
            <div className="flex-1 space-y-3 overflow-y-auto pr-1">
              {labelsLoading ? (
                <div className="text-neutral-secondary-mid py-4 text-center text-xs">
                  Labels laden…
                </div>
              ) : labelsByType.length === 0 ? (
                <div className="text-neutral-secondary-mid py-4 text-center text-xs">
                  Geen labels gevonden
                </div>
              ) : (
                labelsByType.map(({ type, items }) => (
                  <div key={type} className="space-y-1">
                    <div className="text-neutral-secondary-mid text-[10px] uppercase tracking-wide">
                      {type}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {items.map((label) => (
                        <button
                          key={label.id}
                          type="button"
                          draggable
                          onDragStart={handleLabelDragStart(label)}
                          onDragEnd={handleLabelDragEnd}
                          onClick={() => {
                            if (viewMode === "kostenplaats") {
                              toast.info(
                                "Sleep dit label op een kostenplaats om een budgetlijn te plannen",
                              );
                              return;
                            }
                            addRowFromLabel(label);
                          }}
                          className={`inline-flex cursor-grab items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium transition-shadow hover:shadow-sm active:cursor-grabbing ${
                            LABEL_TYPE_TONE[type] ??
                            LABEL_TYPE_TONE.algemeen
                          }`}
                          title="Klik of sleep naar de verdeling"
                        >
                          <Tag size={10} weight="fill" />
                          {label.name}
                        </button>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
            <p className="text-neutral-secondary-mid text-[11px] leading-snug">
              {viewMode === "label"
                ? "Sleep een label naar het canvas of klik om een budgetlijn toe te voegen."
                : "Sleep een label op een kostenplaats om er een budgetlijn voor te plannen."}
            </p>
          </aside>

          {/* RIGHT: split canvas — label-first (klassiek) of kostenplaats-first */}
          {viewMode === "kostenplaats" ? (
            <KostenplaatsVerdelingCanvas
              groups={unitGroups}
              onChange={setUnitGroups}
              unitsById={unitsById}
              bucketTotal={Number(currentBucket?.totalAmount) || 0}
              getDraggedUnit={getDraggedUnit}
              getDraggedLabel={getDraggedLabel}
              isAllUnitsDrag={isAllUnitsDrag}
              clearDrag={clearDrag}
              allUnitIds={allUnitIds}
            />
          ) : (
          <main
            onDragOver={handleCanvasDragOver}
            onDrop={handleCanvasDrop}
            className={`flex flex-col gap-3 overflow-y-auto rounded-lg border-2 border-dashed p-3 transition-colors ${
              dragLabelRef.current
                ? "border-moss-primary bg-moss-primary/5"
                : "border-neutral-200 bg-white"
            }`}
          >
            {rows.length === 0 ? (
              <div className="text-neutral-secondary-mid flex flex-1 flex-col items-center justify-center gap-2 text-center text-sm">
                <Coin size={36} weight="duotone" className="text-neutral-300" />
                <div className="font-medium">Sleep labels hierheen</div>
                <div className="text-xs">
                  Of klik op een label links om een nieuwe budgetlijn toe te
                  voegen.
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <div className="text-neutral-primary text-sm font-medium">
                    {rows.length} regel{rows.length === 1 ? "" : "s"}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={splitRemainingEqually}
                      className="h-7 text-xs"
                      title="Verdeel het volledige potbudget gelijk over alle regels"
                    >
                      <ArrowsCounterClockwise size={12} className="mr-1" />
                      Verdeel gelijk
                    </Button>
                  </div>
                </div>

                {rows.map((row, idx) => (
                  <SplitRowCard
                    key={row.rowId}
                    row={row}
                    index={idx}
                    unitsById={unitsById}
                    bucketTotal={Number(currentBucket?.totalAmount) || 0}
                    onChange={(patch) => updateRow(row.rowId, patch)}
                    onDelete={() => removeRow(row.rowId)}
                    onFillRest={() => fillRestOnRow(row.rowId)}
                    onRemoveUnit={(uid) => removeUnitFromRow(row.rowId, uid)}
                    onSetUnitPercentage={(uid, pct) =>
                      setUnitPercentage(row.rowId, uid, pct)
                    }
                    onSetUnitAmount={(uid, amt) =>
                      setUnitAmount(row.rowId, uid, amt)
                    }
                    onSplitUnitsEqually={() =>
                      splitUnitsEquallyOnRow(row.rowId)
                    }
                    isAllUnitsDragActive={() => dragAllUnitsRef.current}
                    isUnitDragActive={() => dragUnitRef.current}
                    onAcceptAllUnits={() => {
                      applyAllUnitsToRow(row.rowId);
                      dragAllUnitsRef.current = false;
                    }}
                    onAcceptUnit={(unitId) => {
                      addUnitToRow(row.rowId, unitId);
                      dragUnitRef.current = null;
                    }}
                  />
                ))}
              </>
            )}
          </main>
          )}

          {/* OPTIONAL: read-only verdeling per kostenplaats, live from the
              active view's draft (works for both label- and unit-first). */}
          {showUnitSummary && (
            <UnitVerdelingSummary
              items={itemsToCreate}
              unitsById={unitsById}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// Helpers
// ============================================================================

interface BudgetMeterProps {
  total: number;
  usedBeforeDraft: number;
  draftAmount: number;
  remaining: number;
  overspent: boolean;
}

const BudgetMeter: React.FC<BudgetMeterProps> = ({
  total,
  usedBeforeDraft,
  draftAmount,
  remaining,
  overspent,
}) => {
  const safeTotal = total > 0 ? total : 1;
  const usedPct = Math.min(100, (usedBeforeDraft / safeTotal) * 100);
  const draftPct = Math.min(100 - usedPct, (draftAmount / safeTotal) * 100);
  return (
    <div className="mt-3 rounded-md border border-neutral-200 bg-neutral-50 p-3">
      <div className="text-neutral-secondary-mid mb-2 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-xs">
        <span>
          Totaal:{" "}
          <span className="text-neutral-primary font-semibold">
            {formatCurrency(total)}
          </span>
        </span>
        {usedBeforeDraft > 0 && (
          <span>
            Reeds verdeeld:{" "}
            <span className="text-neutral-primary font-semibold">
              {formatCurrency(usedBeforeDraft)}
            </span>
          </span>
        )}
        <span>
          Nu in concept:{" "}
          <span className="text-moss-primary font-semibold">
            {formatCurrency(draftAmount)}
          </span>
        </span>
        <span>
          Resterend:{" "}
          <span
            className={`font-semibold ${
              overspent ? "text-rose-600" : "text-neutral-primary"
            }`}
          >
            {formatCurrency(remaining)}
          </span>
        </span>
      </div>
      <div className="relative h-2 w-full overflow-hidden rounded-full bg-neutral-200">
        <div
          className="absolute inset-y-0 left-0 bg-neutral-400"
          style={{ width: `${usedPct}%` }}
          title="Reeds verdeeld"
        />
        <div
          className="bg-moss-primary absolute inset-y-0"
          style={{ left: `${usedPct}%`, width: `${draftPct}%` }}
          title="Nu in concept"
        />
        {overspent && (
          <div className="bg-rose-500 absolute inset-y-0 right-0 w-1.5" />
        )}
      </div>
    </div>
  );
};

interface SplitRowCardProps {
  row: SplitRow;
  index: number;
  unitsById: Map<string, UnitDTO>;
  bucketTotal: number;
  onChange: (patch: Partial<SplitRow>) => void;
  onDelete: () => void;
  onFillRest: () => void;
  onRemoveUnit: (unitId: string) => void;
  onSetUnitPercentage: (unitId: string, pct: number) => void;
  onSetUnitAmount: (unitId: string, amount: number) => void;
  onSplitUnitsEqually: () => void;
  isAllUnitsDragActive?: () => boolean;
  isUnitDragActive?: () => string | null;
  onAcceptAllUnits?: () => void;
  onAcceptUnit?: (unitId: string) => void;
}

const SplitRowCard: React.FC<SplitRowCardProps> = ({
  row,
  index,
  unitsById,
  bucketTotal,
  onChange,
  onDelete,
  onFillRest,
  onRemoveUnit,
  onSetUnitPercentage,
  onSetUnitAmount,
  onSplitUnitsEqually,
  isAllUnitsDragActive,
  isUnitDragActive,
  onAcceptAllUnits,
  onAcceptUnit,
}) => {
  const [isDropHovering, setIsDropHovering] = useState(false);
  const sharePct = bucketTotal > 0 ? (row.amount / bucketTotal) * 100 : 0;
  const tone =
    LABEL_TYPE_TONE[row.labelType ?? "algemeen"] ?? LABEL_TYPE_TONE.algemeen;

  const isAcceptingDrop = () =>
    !!isAllUnitsDragActive?.() || !!isUnitDragActive?.();

  const handleRowDragOver = (e: React.DragEvent) => {
    if (!isAcceptingDrop()) return;
    e.preventDefault();
    e.stopPropagation();
    if (!isDropHovering) setIsDropHovering(true);
  };
  const handleRowDragLeave = () => {
    if (isDropHovering) setIsDropHovering(false);
  };
  const handleRowDrop = (e: React.DragEvent) => {
    if (!isAcceptingDrop()) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDropHovering(false);
    if (isAllUnitsDragActive?.()) {
      onAcceptAllUnits?.();
      return;
    }
    const draggedUnit = isUnitDragActive?.();
    if (draggedUnit) onAcceptUnit?.(draggedUnit);
  };

  // Sum of percentages for validation indicator.
  const pctSum = row.unitIds.reduce((s, uid) => {
    const v =
      row.unitPercentages[uid] ?? 100 / Math.max(1, row.unitIds.length);
    return s + v;
  }, 0);
  const pctMismatch = row.unitIds.length > 0 && Math.abs(pctSum - 100) > 0.5;

  return (
    <div
      onDragOver={handleRowDragOver}
      onDragLeave={handleRowDragLeave}
      onDrop={handleRowDrop}
      className={`flex flex-col gap-3 rounded-md border p-3 shadow-sm transition-colors ${
        isDropHovering
          ? "border-moss-primary bg-moss-primary/5"
          : "border-neutral-200 bg-white"
      }`}
    >
      {/* Header row: index + label chip + title + amount + delete */}
      <div className="grid grid-cols-[160px_1fr_140px_auto] items-start gap-3">
        <div className="flex flex-col gap-1.5">
          <div className="text-neutral-secondary-mid text-[10px] uppercase tracking-wide">
            #{index + 1}
          </div>
          <span
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${tone}`}
            title={row.labelName}
          >
            <Tag size={10} weight="fill" />
            <span className="break-words leading-tight">{row.labelName}</span>
          </span>
          {/* Strategic-budget badge: every bucket-derived row is strategic by
              definition (the onvoorzien pot is read-only). Shown as a static
              indicator so users see the labeling, no toggle to confuse with. */}
          <span
            className="bg-moss-primary/10 text-moss-primary border-moss-primary/30 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium"
            title="Lijnen uit een strategisch budget zijn altijd strategisch budget"
          >
            <Wallet size={10} weight="fill" />
            Strategisch
          </span>
        </div>

        <div className="flex flex-col gap-1.5">
          <Input
            value={row.title}
            onChange={(e) => onChange({ title: e.target.value })}
            placeholder="Titel van de budgetlijn"
            className="h-8 text-sm"
          />
          <Textarea
            value={row.description}
            onChange={(e) => onChange({ description: e.target.value })}
            placeholder="Korte beschrijving (optioneel)"
            rows={2}
            className="min-h-[44px] resize-y text-xs"
          />
        </div>

        <div className="flex flex-col items-end gap-1">
          <div className="relative w-full">
            <span className="text-neutral-secondary-mid pointer-events-none absolute inset-y-0 left-2 flex items-center text-xs">
              €
            </span>
            <Input
              type="number"
              min={0}
              step={1000}
              value={row.amount || ""}
              onChange={(e) =>
                onChange({ amount: Number(e.target.value || 0) })
              }
              placeholder="0"
              className="h-8 pl-5 text-right text-sm tabular-nums"
            />
          </div>
          <div className="text-neutral-secondary-mid text-[10px] tabular-nums">
            {sharePct.toFixed(1)}% van strategisch budget
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onFillRest}
            className="h-6 px-1.5 text-[10px]"
            title="Vul met het resterende budget"
          >
            <Plus size={10} className="mr-0.5" />
            Vul rest
          </Button>
        </div>

        <button
          type="button"
          onClick={onDelete}
          className="hover:text-rose-600 self-start rounded-md p-1.5 text-neutral-400 transition-colors hover:bg-rose-50"
          title="Verwijder regel"
        >
          <Trash size={14} />
        </button>
      </div>

      {/* Unit chips area */}
      <div className="rounded-md border border-dashed border-neutral-200 bg-neutral-50/60 p-2">
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <div className="text-neutral-secondary-mid text-[10px] font-semibold uppercase tracking-wide">
            Kostenplaatsen
            <span className="text-neutral-400 ml-1.5 normal-case font-normal italic tracking-normal">
              {row.unitIds.length === 0
                ? "sleep units uit de bovenste balk hierheen"
                : `${row.unitIds.length} unit${
                    row.unitIds.length === 1 ? "" : "s"
                  } gekoppeld`}
            </span>
          </div>
          {row.unitIds.length > 1 && (
            <div className="flex items-center gap-2">
              {pctMismatch && (
                <span className="text-rose-600 text-[10px] font-medium tabular-nums">
                  Σ {pctSum.toFixed(1)}%
                </span>
              )}
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={onSplitUnitsEqually}
                className="h-6 px-2 text-[10px]"
                title="Verdeel het bedrag gelijk over alle units"
              >
                <ArrowsCounterClockwise size={10} className="mr-1" />
                Gelijk verdelen
              </Button>
            </div>
          )}
        </div>
        {row.unitIds.length === 0 ? (
          <div className="text-neutral-secondary-mid py-2 text-center text-[11px] italic">
            Nog geen units gekoppeld
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {row.unitIds.map((uid) => {
              const u = unitsById.get(uid);
              const surface = getUnitSurface(u);
              const pct =
                row.unitPercentages[uid] ??
                100 / Math.max(1, row.unitIds.length);
              const amt = Math.round(((row.amount || 0) * pct) / 100);
              return (
                <UnitAllocationChip
                  key={uid}
                  unitId={uid}
                  surface={surface}
                  pct={pct}
                  amount={amt}
                  onSetPct={(v) => onSetUnitPercentage(uid, v)}
                  onSetAmount={(v) => onSetUnitAmount(uid, v)}
                  onRemove={() => onRemoveUnit(uid)}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ----- Per-unit allocation chip -----

interface UnitAllocationChipProps {
  unitId: string;
  surface: number | null;
  pct: number;
  amount: number;
  onSetPct: (v: number) => void;
  onSetAmount: (v: number) => void;
  onRemove: () => void;
}

const UnitAllocationChip: React.FC<UnitAllocationChipProps> = ({
  unitId,
  surface,
  pct,
  amount,
  onSetPct,
  onSetAmount,
  onRemove,
}) => {
  // Mirror inbound props to local state so the input doesn't fight rounding.
  const [pctDraft, setPctDraft] = useState(pct.toFixed(1));
  const [amtDraft, setAmtDraft] = useState(String(amount));

  // Keep local state synced when the parent recomputes (e.g. equal-split,
  // amount change, sibling unit edit).
  useEffect(() => {
    setPctDraft(pct.toFixed(1));
  }, [pct]);
  useEffect(() => {
    setAmtDraft(String(amount));
  }, [amount]);

  return (
    <div
      className="bg-moss-primary/5 border-moss-primary/30 flex items-center gap-1.5 rounded-md border px-2 py-1.5 text-[11px]"
      title={surface ? `${unitId} — ${surface} m²` : unitId}
    >
      <Cube size={11} weight="duotone" className="text-moss-primary shrink-0" />
      <div className="flex flex-col leading-tight">
        <span className="text-moss-primary font-semibold tabular-nums">
          {unitId}
        </span>
        {surface !== null && (
          <span className="text-neutral-secondary-mid text-[9px] tabular-nums">
            {surface} m²
          </span>
        )}
      </div>
      <div className="bg-moss-primary/20 mx-0.5 h-7 w-px" />
      <div className="flex items-center gap-0.5">
        <span className="text-neutral-secondary-mid text-[10px]">€</span>
        <input
          type="number"
          min={0}
          step={100}
          value={amtDraft}
          onChange={(e) => setAmtDraft(e.target.value)}
          onBlur={() => onSetAmount(Number(amtDraft) || 0)}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          }}
          className="h-6 w-16 rounded border border-neutral-200 bg-white px-1 text-right text-[11px] tabular-nums focus:border-moss-primary focus:outline-none"
          title="Pas het bedrag aan voor deze unit"
        />
      </div>
      <div className="flex items-center gap-0.5">
        <input
          type="number"
          min={0}
          max={100}
          step={0.5}
          value={pctDraft}
          onChange={(e) => setPctDraft(e.target.value)}
          onBlur={() => onSetPct(Number(pctDraft) || 0)}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          }}
          className="h-6 w-12 rounded border border-neutral-200 bg-white px-1 text-right text-[11px] tabular-nums focus:border-moss-primary focus:outline-none"
          title="Pas het percentage aan voor deze unit"
        />
        <span className="text-neutral-secondary-mid text-[10px]">%</span>
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="hover:text-rose-600 ml-0.5 rounded p-0.5 text-neutral-400 transition-colors hover:bg-rose-50"
        title="Verwijder unit"
      >
        <X size={11} />
      </button>
    </div>
  );
};

export default BudgetSplitDesigner;
